import { useState, useEffect } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import Lottie from 'lottie-react';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

import AgeGroupIcon from '../../../image/AgeGroupIcon.svg';
import LoadingAnimation from '../../../Animation/NewLoading.json';

export default function MemberEngagementBox(props) {
  const {
    calcDateText,
    engagementReserves,
    isOpenPartnerGymSales,
    isEngagementLoading,
    setIsEngagementLoading,
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [allAgeGroups, setAllAgeGroups] = useState([]);
  const [engagementStates, setEngagementStates] = useState([]);
  const [engagementTotalCount, setEngagementTotalCount] = useState(0);
  const [engagementMaxCount, setEngagementMaxCount] = useState(0);
  const [engagementMaxText, setEngagementMaxText] = useState('');

  // 연령별 이용자 분석 데이터 가공 //
  useEffect(() => {
    let year = parseInt(moment().format('YYYY'));

    let engagementStateList = [
      { text: t('statistics.engagement.ageUnder10'), startYear: year - 8, endYear: year, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age10s'), startYear: year - 18, endYear: year - 9, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age20s'), startYear: year - 28, endYear: year - 19, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age30s'), startYear: year - 38, endYear: year - 29, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age40s'), startYear: year - 48, endYear: year - 39, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age50s'), startYear: year - 58, endYear: year - 49, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age60s'), startYear: year - 68, endYear: year - 59, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age70s'), startYear: year - 78, endYear: year - 69, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age80s'), startYear: year - 88, endYear: year - 79, totalCount: 0, usages: [] },
      { text: t('statistics.engagement.age90s'), startYear: year - 98, endYear: year - 89, totalCount: 0, usages: [] },
    ];

    for (let i = 0; i < engagementReserves.length; i++) {
      let birthYear = parseInt(engagementReserves[i].birthDay.slice(0, 4));

      for (let j = 0; j < engagementStateList.length; j++) {
        if (engagementStateList[j].startYear <= birthYear && birthYear <= engagementStateList[j].endYear) {
          let isExist = false;

          for (let h = 0; h < engagementStateList[j].usages.length; h++) {
            if (engagementStateList[j].usages[h].membershipId === engagementReserves[i].membershipId) {
              engagementStateList[j].totalCount += 1;
              engagementStateList[j].usages[h].count += 1;
              isExist = true;
              break;
            }
          }

          if (!isExist) {
            engagementStateList[j].totalCount += 1;

            engagementStateList[j].usages.push({
              membershipId: engagementReserves[i].membershipId,
              membershipName: engagementReserves[i].membershipName,
              count: 1
            });
          }

          break;
        }
      }
    }

    let grandTotal = 0;
    let maxText = t('statistics.engagement.ageUnder10');
    let maxCount = 0;

    for (let i = 0; i < engagementStateList.length; i++) {
      engagementStateList[i].usages.sort((a, b) => b.count - a.count || a.membershipId - b.membershipId);
      grandTotal += engagementStateList[i].totalCount;

      if (engagementStateList[i].totalCount > maxCount) {
        maxText = engagementStateList[i].text;
        maxCount = engagementStateList[i].totalCount;
      }
    }

    setAllAgeGroups(engagementStateList.slice(0, 9));

    let finalEngagementStates = engagementStateList.filter(group => group.usages.length > 0);

    setEngagementStates(finalEngagementStates);
    setEngagementTotalCount(grandTotal);
    setEngagementMaxCount(maxCount);
    setEngagementMaxText(maxText);
    setIsEngagementLoading(false);
  }, [engagementReserves]);

  // 연령별 비율 계산 //
  function calcPercentage(count) {
    if (engagementTotalCount === 0) return '0';

    return (count / engagementTotalCount * 100).toFixed(1);
  }

  // 프로그레스 바 너비 비율 계산 //
  function calcBarWidth(count) {
    if (engagementMaxCount === 0) return 0;

    return Math.min((count / engagementMaxCount) * 100, 100);
  }

  // 선호 비중 계산 //
  function calcPreference(usageCount, groupTotalCount) {
    if (groupTotalCount === 0) return '0';

    return (usageCount / groupTotalCount * 100).toFixed(0);
  }

  // 숫자 콤마 포맷팅 //
  function formatNumber(num) {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // 프로그레스 바 카드 렌더링 //
  function renderProgressBarCard(data, index) {
    let percentage = calcPercentage(data.totalCount);
    let barWidth = calcBarWidth(data.totalCount);

    return (
      <ProgressBarCardWrapper key={'ProgressBar-' + index}>
        <ProgressBarInfoRow>
          <ProgressBarLabelWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{data.text}</Text>
            <PercentageWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.primary}>{percentage}</Text>
              <PercentageSuffix>
                <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
              </PercentageSuffix>
            </PercentageWrapper>
          </ProgressBarLabelWrapper>
          <CountWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>{formatNumber(data.totalCount)}</Text>
            <CountSuffix>
              <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.subText}>{t('statistics.engagement.memberUnit')}</Text>
            </CountSuffix>
          </CountWrapper>
        </ProgressBarInfoRow>
        <ProgressBarTrack>
          {
            data.totalCount > 0 &&
            <ProgressBarFill width={barWidth} />
          }
        </ProgressBarTrack>
      </ProgressBarCardWrapper>
    );
  }

  // 테이블 연령 그룹 행 렌더링 //
  function renderAgeGroupRow(data, index) {
    let percentage = calcPercentage(data.totalCount);
    let isLast = index === engagementStates.length - 1;

    return (
      <AgeGroupRow key={'AgeGroup-' + index}>
        <AgeGroupLabelCell $isLast={isLast}>
          <AgeGroupLabelContent>
            <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>{data.text}</Text>
            <AgeGroupSubInfo>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                {formatNumber(data.totalCount) + t('statistics.engagement.memberUnit')}
              </Text>
              <PercentageWrapper>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.primary}>{percentage}</Text>
                <PercentageSuffix>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
                </PercentageSuffix>
              </PercentageWrapper>
            </AgeGroupSubInfo>
          </AgeGroupLabelContent>
        </AgeGroupLabelCell>
        <MembershipColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Membership-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{usage.membershipName}</Text>
              </TableDataCell>
            ))
          }
        </MembershipColumnWrapper>
        <UsageColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Usage-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {formatNumber(usage.count) + t('statistics.countUnit')}
                </Text>
              </TableDataCell>
            ))
          }
        </UsageColumnWrapper>
        <PreferenceColumnWrapper>
          {
            data.usages.map((usage, usageIndex) => (
              <TableDataCell
                key={'Preference-' + usageIndex}
                $isLast={isLast && usageIndex === data.usages.length - 1}
                $isGroupLast={usageIndex === data.usages.length - 1}
                $center>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                  {calcPreference(usage.count, data.totalCount) + '%'}
                </Text>
              </TableDataCell>
            ))
          }
        </PreferenceColumnWrapper>
      </AgeGroupRow>
    );
  }

  return (
    <TopLevelWrapper $isDark={isDark}>
      {
        isEngagementLoading ?
          <AnimationBox>
            <LottieWrapper>
              <Lottie animationData={LoadingAnimation} loop={true} />
            </LottieWrapper>
          </AnimationBox>
          :
          isOpenPartnerGymSales ?
            <ContentWrapper>
              <TitleRow>
                <Text fontFamily='NotoSansKR-Bold' fontSize={20} color={theme.text}>
                  {calcDateText() + ' ' + t('statistics.engagement.ageAnalysis')}
                </Text>
              </TitleRow>
              <SubtitleRow>
                <AgeRangeImage src={AgeGroupIcon} />
                <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.text}>
                  {t('statistics.engagement.mostAge') + ' : ' + engagementMaxText}
                </Text>
                <PercentageWrapper>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={20} color={theme.primary}>
                    {engagementTotalCount > 0 ? (engagementMaxCount / engagementTotalCount * 100).toFixed(1) : '0'}
                  </Text>
                  <PercentageSuffix>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={theme.primary}>%</Text>
                  </PercentageSuffix>
                </PercentageWrapper>
              </SubtitleRow>
              <ProgressBarGridWrapper>
                <ProgressBarRow>
                  {allAgeGroups.slice(0, 3).map((data, index) => renderProgressBarCard(data, index))}
                </ProgressBarRow>
                <ProgressBarRow>
                  {allAgeGroups.slice(3, 6).map((data, index) => renderProgressBarCard(data, index + 3))}
                </ProgressBarRow>
                <ProgressBarRow>
                  {allAgeGroups.slice(6, 9).map((data, index) => renderProgressBarCard(data, index + 6))}
                </ProgressBarRow>
              </ProgressBarGridWrapper>
              <TableSectionWrapper>
                <TableTitleWrapper>
                  <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
                    {t('statistics.engagement.ageUsageStats')}
                  </Text>
                </TableTitleWrapper>
                <TableContentWrapper>
                  <TableHeaderRow>
                    <TableHeaderCell width={200}>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colAge')}
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderCell width={330}>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colMembership')}
                      </Text>
                    </TableHeaderCell>
                    <TableHeaderFlexCell>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colUsageRecord')}
                      </Text>
                    </TableHeaderFlexCell>
                    <TableHeaderFlexCell>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>
                        {t('statistics.engagement.colPreference')}
                      </Text>
                    </TableHeaderFlexCell>
                  </TableHeaderRow>
                  <TableDividerLine />
                  <TableBodyWrapper>
                    {engagementStates.map((data, index) => renderAgeGroupRow(data, index))}
                  </TableBodyWrapper>
                </TableContentWrapper>
              </TableSectionWrapper>
            </ContentWrapper>
            :
            <NotOpenWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivate')}</Text>
              <Text fontFamily='NotoSansKR-Regular' fontSize={15} color={theme.subText}>{t('statistics.partnerGymPrivateDesc')}</Text>
            </NotOpenWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  margin: 38px 0;
  padding: 20px 40px;

  width: 100%;
  max-width: 960px;

  gap: 10px;

  border-radius: 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '0 40px 50px rgba(240, 232, 227, 0.5)'};
  background-color: ${props => props.theme.subBackground};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;

  gap: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  overflow: hidden;

  width: 100%;
`;

const SubtitleRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const AgeRangeImage = styled.img`
  width: 32px;
  height: 30px;

  margin-top: 5px;
`;

const PercentageWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 1px;
`;

const PercentageSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  gap: 5px;
`;

const CountSuffix = styled.div`
  display: flex;
  align-items: flex-end;

  padding-bottom: 2px;
`;

const ProgressBarGridWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  padding: 20px 0;

  width: 100%;

  gap: 10px;
`;

const ProgressBarRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 40px;
`;

const ProgressBarCardWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  flex: 1;
  min-width: 0;
`;

const ProgressBarInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ProgressBarLabelWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  flex: 1;
  min-width: 0;

  gap: 10px;
`;

const ProgressBarTrack = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.buttonFill3};
`;

const ProgressBarFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;

  width: ${props => props.width}%;
  height: 10px;

  border-radius: 1000px;
  background-color: ${props => props.theme.primary};
`;

const TableSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  overflow: hidden;

  width: 100%;

  gap: 10px;
`;

const TableTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;
  overflow: hidden;

  width: 100%;
`;

const TableContentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  overflow: hidden;

  width: 100%;

  border-radius: 5px;
`;

const TableHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;
  overflow: hidden;

  width: 100%;
  height: 30px;

  border-radius: 5px 5px 0 0;
`;

const TableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;

  padding: 0 10px;

  width: ${props => props.width}px;
  height: 16px;
`;

const TableHeaderFlexCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  min-width: 0;
  height: 16px;
`;

const TableDividerLine = styled.div`
  width: 100%;
  height: 1px;

  margin: 7px 0;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const TableBodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  width: 100%;
  max-height: 450px;

  overflow-y: scroll;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 200px;
  }
  
  border-radius: 20px;
`;

const AgeGroupRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: stretch;

  width: 100%;
`;

const AgeGroupLabelCell = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;
  overflow: hidden;

  padding: 20px 10px;

  width: 200px;

  background-color: ${props => props.theme.buttonFill};
  border-bottom: ${props => props.$isLast ? 'none' : `1px solid ${props.theme.primaryAlpha20}`};
`;

const AgeGroupLabelContent = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  padding: 10px 0;
`;

const AgeGroupSubInfo = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const MembershipColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;
  justify-content: center;

  width: 350px;

  border-right: 1px solid ${props => props.theme.line};
`;

const UsageColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;

  border-right: 1px solid ${props => props.theme.line};
`;

const PreferenceColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  align-items: flex-start;
  justify-content: center;

  min-width: 0;
`;

const TableDataCell = styled.div`
  display: flex;
  flex: 1;

  align-items: center;
  justify-content: ${props => props.$center ? 'center' : 'flex-start'};
  overflow: hidden;

  padding: 10px;

  width: calc(100% - 20px);
  min-height: 0;

  border-bottom: ${props => {
    if (props.$isLast) return 'none';
    if (props.$isGroupLast) return `1px solid ${props.theme.primaryAlpha20}`;
    return `1px solid ${props.theme.line}`;
  }};
`;

const AnimationBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 300px;
`;

const LottieWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 160px;
  height: 160px;
`;

const NotOpenWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  padding: 40px 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;
  cursor: default;
`;